import { useParams, Navigate, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { SEO, Breadcrumb, ArticleSchema } from '@/components/seo'
import { Calendar, Clock, ArrowLeft, ArrowRight, Tag } from 'lucide-react'
import { buttonStyles } from '@/components/ui'
import { getBlogPostBySlug, getRecentBlogPosts } from '@/data/blog'

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getBlogPostBySlug(slug) : null
  const recentPosts = getRecentBlogPosts(3).filter((p) => p.slug !== slug)

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        canonical={`/blog/${slug}`}
        ogType="article"
        publishedTime={post.date}
        modifiedTime={post.updated}
        keywords={post.tags.join(', ')}
      />
      <ArticleSchema
        headline={post.title}
        description={post.description}
        image="/og-image.png"
        datePublished={post.date}
        dateModified={post.updated}
      />

      <article className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: 'Blog', href: '/blog' },
                { label: post.title },
              ]}
            />

            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full capitalize">
                  {post.category}
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                {post.title}
              </h1>

              <p className="mt-4 text-lg text-gray-600">
                {post.description}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                {post.updated && post.updated !== post.date && (
                  <span className="text-gray-400">
                    (Updated {new Date(post.updated).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })})
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {post.readTime} read
                </span>
              </div>

              {/* Tags */}
              <div className="mt-4 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Table of Contents hint */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Quick navigation:</strong> Use headings below to jump to specific sections.
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-gray max-w-none
              prose-headings:font-bold prose-headings:text-gray-900
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2
              prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4
              prose-li:text-gray-600 prose-li:my-1
              prose-a:text-primary-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
              prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:overflow-x-auto
              prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-primary-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic
              prose-table:border-collapse prose-table:w-full
              prose-th:bg-gray-100 prose-th:border prose-th:border-gray-300 prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold
              prose-td:border prose-td:border-gray-300 prose-td:px-4 prose-td:py-2
              prose-hr:border-gray-200 prose-hr:my-8
              prose-img:rounded-lg prose-img:shadow-md
            ">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.content}
              </ReactMarkdown>
            </div>

            {/* CTA */}
            <div className="mt-12 p-6 bg-gradient-to-r from-primary-50 to-indigo-50 rounded-xl border border-primary-100">
              <h3 className="text-xl font-bold text-gray-900">Ready to compare AI detectors?</h3>
              <p className="mt-2 text-gray-600">
                Test your images against multiple AI detectors and see real-time accuracy benchmarks.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/arena" className={buttonStyles()}>
                  Try the Arena
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/" className={buttonStyles({ variant: 'secondary' })}>
                  View Rankings
                </Link>
              </div>
            </div>

            {/* Related Posts */}
            {recentPosts.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Related Articles</h3>
                <div className="grid gap-4">
                  {recentPosts.slice(0, 2).map((relatedPost) => (
                    <Link
                      key={relatedPost.slug}
                      to={`/blog/${relatedPost.slug}`}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                    >
                      <h4 className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                        {relatedPost.title}
                      </h4>
                      <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                        {relatedPost.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Back to Blog */}
            <div className="mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </article>
    </>
  )
}
